import * as React from "react";
import { useTransition, animated } from "react-spring";
import "./ExpandableTableRow.css";

export interface ExpandableTableRowProps {
    expandedContent: React.ReactElement;
}

export const ExpandableTableRow = ({
    children,
    expandedContent
}: React.PropsWithChildren<ExpandableTableRowProps>) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    const isVisibleOnMount = React.useRef(isExpanded && false);
    const containerRef = React.useRef(null);
    const innerRef = React.useRef(null);

    const visibleStyle = { height: "auto", opacity: 1, overflow: "visible" };
    const hiddenStyle = { opacity: 0, height: 0, overflow: "hidden" };

    const getElementHeight = ref => {
        return ref.current ? ref.current.getBoundingClientRect().height : 0;
    };

    const transitions = useTransition<any, any>(isExpanded, null, {
        unique: true,
        enter: () => async (next, cancel) => {
            const height = getElementHeight(innerRef);

            cancel();

            if (height) {
                await next({ height, opacity: 1, overflow: "hidden" });
                await next(visibleStyle);
            }
        },
        leave: () => async (next, cancel) => {
            const height = getElementHeight(containerRef);

            cancel();

            await next({ height, overflow: "hidden" });
            await next(hiddenStyle);

            isVisibleOnMount.current = false;
        },
        from: isVisibleOnMount.current ? visibleStyle : hiddenStyle
    });

    const toggleExpander = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <>
            <tr style={{ cursor: 'pointer' }} onClick={toggleExpander}>
                <td className="col-md-1"><span className={isExpanded ? "fas fa-chevron-up" : "fas fa-chevron-down"}></span>&nbsp;</td>
                {children}
            </tr>
            {transitions.map(({ item: show, props: springProps, key }) => {
                if (show) {
                    return (
                        <animated.tr ref={containerRef} key={key} style={springProps}>
                            <animated.td className="inner" ref={innerRef} colSpan={100}>
                                {expandedContent}
                            </animated.td>
                        </animated.tr>
                    );
                }

                return null;
            })}
        </>
    );
};
